import {useMutation} from "@apollo/client";
import pluralize from "pluralize";
import {Fragment} from "react";
import toast from "react-hot-toast";

import {Alert} from "@/components/alert/Alert.tsx";
import {Card} from "@/components/card/Card.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {ActionDialog} from "@/components/overlay/ActionDialog.tsx";
import {StatusPill} from "@/components/statusPill/StatusPill.tsx";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {GQL_GLOBAL_ALERT_STATE_BULK_SET_IGNORE, GQL_GLOBAL_ALERT_STATES} from "@/lib/queries/alerting.ts";
import {GQL_DASHBOARD_STATS} from "@/lib/queries/website.ts";
import {ALERT_STATUS_TO_PILL_STATUS, GlobalAlertState} from "@/lib/types/alerting.ts";
import {ResponseStatus} from "@/lib/types/common.ts";

import styles from "./BulkIgnoreAlertsDialog.module.scss";

export interface BulkIgnoreAlertsDialogProps {
  alerts: GlobalAlertState[];
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const BulkIgnoreAlertsDialog = ({alerts, show, onClose, onSuccess}: BulkIgnoreAlertsDialogProps) => {
  const [bulkSetIgnore, {loading}] = useMutation(GQL_GLOBAL_ALERT_STATE_BULK_SET_IGNORE, {
    refetchQueries: [GQL_GLOBAL_ALERT_STATES, GQL_DASHBOARD_STATS],
  });

  const ignorableAlerts = alerts.filter((alert) => alert.status !== "ignored");
  const skippedCount = alerts.length - ignorableAlerts.length;

  const handleConfirm = async () => {
    const response = await bulkSetIgnore({
      variables: {ids: ignorableAlerts.map((alert) => alert.id), ignored: true},
    });
    if (response.data?.response?.status === ResponseStatus.SUCCESS) {
      onSuccess();
      onClose();
    } else {
      toast.error(response.data?.response?.message || "Failed to ignore alerts.");
    }
  };

  return (
    <ActionDialog
      show={show}
      title="Ignore Alerts"
      loading={loading}
      onCancel={onClose}
      onConfirm={handleConfirm}
      disableConfirmButton={ignorableAlerts.length === 0}
      size="lg"
    >
      <p className="pulseTextBody mb-4">
        Are you sure you want to ignore the selected Alerts? You won't receive any updates or notifications for{" "}
        <span className="pulseTextBodyBold">{pluralize("Alert", ignorableAlerts.length, true)}</span> until you resume
        them. You can change this anytime.
      </p>
      {ignorableAlerts.length > 0 && (
        <div className={styles.scroll}>
          <Card variant="defaultBorderless" className={styles.card}>
            {ignorableAlerts.map((alert, index) => (
              <Fragment key={alert.id}>
                {index > 0 && <Divider color="subMenu" />}
                <div className="d-flex justify-content-between align-items-center gap-3">
                  <div className="ellipsis-overflow">
                    <div className="ellipsis-overflow pulseTextBodySmall pulse-color-gray-900 mb-1">
                      {alert.service.name}
                    </div>
                    <Tooltip tooltipContent={alert.service.msp_address} delayMs={500} onModal>
                      <div className="ellipsis-overflow pulseTextBodySmall pulse-color-gray-600">
                        {alert.service.msp_address}
                      </div>
                    </Tooltip>
                  </div>
                  <StatusPill
                    type="incident"
                    variant="textAndIcon"
                    status={ALERT_STATUS_TO_PILL_STATUS[alert.status]}
                  />
                </div>
              </Fragment>
            ))}
          </Card>
        </div>
      )}
      {skippedCount > 0 && (
        <Alert variant="maintenance" className="mt-4 mb-0">
          Already-ignored Alerts in your selection are skipped automatically.
        </Alert>
      )}
    </ActionDialog>
  );
};
